import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { UseFormReturn, useFieldArray } from 'react-hook-form';

interface ApartmentsSectionProps {
  form: UseFormReturn<any>;
  onChange?: () => void;  // <-- Ajout prop onChange facultative
}

const ApartmentsSection: React.FC<ApartmentsSectionProps> = ({ form, onChange }) => {
  const { fields: apartmentFields, append: appendApartment, remove: removeApartment } = useFieldArray({
    control: form.control,
    name: 'apartments',
  });
  const { fields: studioFields, append: appendStudio, remove: removeStudio } = useFieldArray({
    control: form.control,
    name: 'studios',
  });
  const { fields: duplexFields, append: appendDuplex, remove: removeDuplex } = useFieldArray({
    control: form.control,
    name: 'duplexes',
  });

  const [activeList, setActiveList] = useState<'apartments' | 'studios' | 'duplexes'>('apartments');

  const getDefaultItem = () => ({
    number: '',
    floor: '',
    rooms: '',
    surface: '',
    salons: '',
    bathrooms: '',
    price: '',
  });

  useEffect(() => {
    if (activeList === 'apartments' && apartmentFields.length === 0) {
      appendApartment(getDefaultItem());
      onChange?.();
    } else if (activeList === 'studios' && studioFields.length === 0) {
      appendStudio(getDefaultItem());
      onChange?.();
    } else if (activeList === 'duplexes' && duplexFields.length === 0) {
      appendDuplex(getDefaultItem());
      onChange?.();
    }
  }, [activeList, apartmentFields.length, studioFields.length, duplexFields.length, appendApartment, appendStudio, appendDuplex, onChange]);

  const renderItem = (field: any, index: number, removeFn: (index: number) => void) => (
    <div key={field.id} className="border p-4 rounded-md space-y-4 bg-gray-50 dark:bg-gray-800">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name={`${activeList}.${index}.number`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>N°</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  className="bg-white dark:bg-gray-800 text-gray-800 dark:text-black border-gray-300 dark:border-gray-600"
                  onChange={(e) => {
                    field.onChange(e);
                    onChange?.();
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={`${activeList}.${index}.floor`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Étage</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  className="bg-white dark:bg-gray-800 text-gray-800 dark:text-black border-gray-300 dark:border-gray-600"
                  onChange={(e) => {
                    field.onChange(e);
                    onChange?.();
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={`${activeList}.${index}.surface`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Surface</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  {...field}
                  className="bg-white dark:bg-gray-800 text-gray-800 dark:text-black border-gray-300 dark:border-gray-600"
                  onChange={(e) => {
                    field.onChange(e);
                    onChange?.();
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={`${activeList}.${index}.rooms`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Chambres</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  {...field}
                  className="bg-white dark:bg-gray-800 text-gray-800 dark:text-black border-gray-300 dark:border-gray-600"
                  onChange={(e) => {
                    field.onChange(e);
                    onChange?.();
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={`${activeList}.${index}.salons`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Salons</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  {...field}
                  className="bg-white dark:bg-gray-800 text-gray-800 dark:text-black border-gray-300 dark:border-gray-600"
                  onChange={(e) => {
                    field.onChange(e);
                    onChange?.();
                  }}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={`${activeList}.${index}.bathrooms`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Salles de bain</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  {...field}
                  className="bg-white dark:bg-gray-800 text-gray-800 dark:text-black border-gray-300 dark:border-gray-600"
                  onChange={(e) => {
                    field.onChange(e);
                    onChange?.();
                  }}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={`${activeList}.${index}.price`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Prix</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  {...field}
                  className="bg-white dark:bg-gray-800 text-gray-800 dark:text-black border-gray-300 dark:border-gray-600"
                  onChange={(e) => {
                    field.onChange(e);
                    onChange?.();
                  }}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
      <div className="flex justify-end gap-3 mt-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            const current = form.getValues(`${activeList}.${index}`);
            if (activeList === 'apartments') appendApartment({ ...current });
            else if (activeList === 'studios') appendStudio({ ...current });
            else appendDuplex({ ...current });
            onChange?.();
          }}
          className="dark:text-black"
        >
          Dupliquer
        </Button>
        <Button
          type="button"
          variant="destructive"
          onClick={() => {
            if (activeList === 'apartments') removeApartment(index);
            else if (activeList === 'studios') removeStudio(index);
            else removeDuplex(index);
            onChange?.();
          }}
        >
          Supprimer
        </Button>
      </div>
    </div>
  );

  const activeFields = activeList === 'apartments' ? apartmentFields : activeList === 'studios' ? studioFields : duplexFields;
  const appendFn = activeList === 'apartments' ? appendApartment : activeList === 'studios' ? appendStudio : appendDuplex;

  return (
    <div className="space-y-6 mt-6">
      <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Détails du bien</h3>

      <div className="flex space-x-4 mb-4">
        <Button
          type="button"
          variant="outline"
          onClick={() => setActiveList('apartments')}
          className={`${
            activeList === 'apartments'
              ? 'bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-black'
              : 'text-gray-800 dark:text-black'
          }`}
        >
          Appartement
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => setActiveList('studios')}
          className={`${
            activeList === 'studios'
              ? 'bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-black'
              : 'text-gray-800 dark:text-black'
          }`}
        >
          Studio
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => setActiveList('duplexes')}
          className={`${
            activeList === 'duplexes'
              ? 'bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-black'
              : 'text-gray-800 dark:text-black'
          }`}
        >
          Duplex
        </Button>
      </div>

      {activeFields.map((field, index) =>
        renderItem(field, index, (indexToRemove) => {
          if (activeList === 'apartments') removeApartment(indexToRemove);
          else if (activeList === 'studios') removeStudio(indexToRemove);
          else removeDuplex(indexToRemove);
          onChange?.();
        })
      )}

      <Button
        type="button"
        onClick={() => {
          appendFn(getDefaultItem());
          onChange?.();
        }}
        variant="outline"
        className="mt-4 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition duration-200"
      >
        <span className="mr-2">➕</span> Ajouter un {activeList === 'apartments' ? 'appartement' : activeList === 'studios' ? 'studio' : 'duplex'}
      </Button>
    </div>
  );
};

export default ApartmentsSection;
